import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { type ReactElement, useCallback, useRef, useState } from "react";

/**
 * Options accepted by the imperative {@link useConfirmDialog} `confirm` call.
 * When `confirmationKeyword` is set, the confirm button stays disabled until
 * the user types that exact keyword (type-to-confirm for destructive actions).
 */
export type ConfirmOptions = {
  readonly title: string;
  readonly description?: string;
  readonly confirmationText?: string;
  readonly cancellationText?: string;
  readonly confirmationKeyword?: string;
  readonly confirmationKeywordPlaceholder?: string;
};

type PendingResolvers = {
  readonly resolve: () => void;
  readonly reject: () => void;
};

/**
 * Headless, self-contained replacement for `material-ui-confirm`'s
 * `useConfirm`, scoped to the internal API-key admin tool.
 *
 * The internal frontend runs on MUI v5 while the only `material-ui-confirm`
 * build resolved in the monorepo targets MUI v6, so this avoids pulling in a
 * peer-mismatched dependency. It reproduces the same imperative contract the
 * callers rely on: `confirm(options)` opens a modal and returns a promise that
 * resolves when the user confirms and rejects when they cancel or dismiss.
 *
 * @returns `confirm` to open the dialog and the `confirmDialog` element that
 * the caller must render exactly once in its tree.
 */
export const useConfirmDialog = (): {
  confirm: (options: ConfirmOptions) => Promise<void>;
  confirmDialog: ReactElement;
} => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [keywordInput, setKeywordInput] = useState("");
  const resolversRef = useRef<PendingResolvers | null>(null);

  const confirm = useCallback(async (next: ConfirmOptions): Promise<void> => {
    setOptions(next);
    setKeywordInput("");
    return new Promise<void>((resolve, reject) => {
      resolversRef.current = { resolve, reject };
    });
  }, []);

  const close = useCallback(() => {
    setOptions(null);
    setKeywordInput("");
    resolversRef.current = null;
  }, []);

  const handleConfirm = useCallback(() => {
    resolversRef.current?.resolve();
    close();
  }, [close]);

  const handleCancel = useCallback(() => {
    resolversRef.current?.reject();
    close();
  }, [close]);

  const keywordRequired = options?.confirmationKeyword;
  const confirmDisabled =
    keywordRequired !== undefined && keywordInput !== keywordRequired;

  const confirmDialog = (
    <Dialog open={options !== null} onClose={handleCancel}>
      {options !== null && (
        <>
          <DialogTitle>{options.title}</DialogTitle>
          <DialogContent>
            {options.description !== undefined && (
              <DialogContentText>{options.description}</DialogContentText>
            )}
            {keywordRequired !== undefined && (
              <TextField
                autoFocus
                fullWidth
                size="small"
                margin="dense"
                value={keywordInput}
                placeholder={options.confirmationKeywordPlaceholder}
                onChange={(e) => {
                  setKeywordInput(e.target.value);
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel}>
              {options.cancellationText ?? "Cancel"}
            </Button>
            <Button
              variant="contained"
              disabled={confirmDisabled}
              onClick={handleConfirm}
            >
              {options.confirmationText ?? "Confirm"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  return { confirm, confirmDialog };
};
